/* =========================================================================
   Tiered Free Gift  (vanilla JS, no jQuery)
   -------------------------------------------------------------------------
   - Watches the AJAX cart subtotal.
   - When it crosses a configured tier ($200 / $400 / $600), lets the customer
     pick N free gift products (6 / 12 / 18) from up to 3 configured products.
   - Adds them to the cart with the line item property `free_products: true`.
   - Trims / removes the gifts automatically if the cart drops below a tier.
   - Locks the quantity of any line that carries `free_products: true` (only
     those lines — a normally-added product of the same variant is untouched).

   Works with Shopify OS 2.0, the Dawn AJAX cart & cart drawer, and any other
   script that hits /cart/{add,change,update,clear}.js (detected via fetch).
   ========================================================================= */

(() => {
  'use strict';

  // The line item property that marks a popup-added free gift.
  const GIFT_PROP = 'free_products';
  const SEEN_TIER_KEY = 'tfg:autoOpenedTier';

  class TieredFreeGift extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.config = this.readConfig();
      if (!this.config || !this.config.enabled || !this.config.products.length) return;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.selection = {}; // { variantId: qty }
      this.currentTier = null; // active tier object or null
      this.busy = false; // guard while we mutate the cart ourselves
      this.evaluateTimer = null;

      // Variant IDs / line keys of the cart lines that carry `free_products`.
      // Derived live from the cart (never from configured product IDs), so the
      // lock is driven ONLY by the line item property.
      this.giftLineVariants = new Set();
      this.giftLineKeys = new Set();
      this.ambiguousVariants = new Set(); // variants on >1 cart line
      this.cartItems = []; // latest /cart.js items, in cart order
      this.lockGiftQty = this.config.lockGiftQuantity !== false;

      this.cacheDom();
      this.buildTrigger();
      this.bindEvents();
      this.watchCart();

      if (this.lockGiftQty) this.setupQuantityLock();

      // Initial state from the current cart.
      this.evaluate(false);
    }

    /* ---- Config ---------------------------------------------------------- */
    readConfig() {
      const el = this.querySelector('[data-tfg-config]');
      if (!el) return null;
      try {
        const cfg = JSON.parse(el.textContent);
        // Sort tiers ascending by threshold so lookups are predictable.
        cfg.tiers = (cfg.tiers || []).filter((t) => t.quantity > 0).sort((a, b) => a.threshold - b.threshold);
        cfg.products = cfg.products || [];
        return cfg;
      } catch (e) {
        console.error('[TieredFreeGift] Invalid config JSON', e);
        return null;
      }
    }

    cacheDom() {
      this.overlay = this.querySelector('[data-tfg-overlay]');
      this.modal = this.querySelector('[data-tfg-modal]');
      this.grid = this.querySelector('[data-tfg-grid]');
      this.headingEl = this.querySelector('[data-tfg-heading]');
      this.subheadingEl = this.querySelector('[data-tfg-subheading]');
      this.counterEl = this.querySelector('[data-tfg-counter]');
      this.submitBtn = this.querySelector('[data-tfg-submit]');
      this.submitLabel = this.querySelector('[data-tfg-submit-label]');
      this.cardTemplate = this.querySelector('[data-tfg-card-template]');
    }

    bindEvents() {
      this.querySelectorAll('[data-tfg-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
      });
      this.submitBtn.addEventListener('click', () => this.addFreeProducts());
    }

    /* ---- Floating re-open trigger --------------------------------------- */
    buildTrigger() {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tfg-trigger';
      btn.innerHTML =
        '<span class="tfg-trigger__gift" aria-hidden="true">&#127873;</span>' +
        '<span data-tfg-trigger-label></span>';
      btn.addEventListener('click', () => this.open());
      document.body.appendChild(btn);
      this.trigger = btn;
      this.triggerLabel = btn.querySelector('[data-tfg-trigger-label]');
    }

    /* ---- Cart watching --------------------------------------------------- */
    watchCart() {
      // 1) Dawn / theme pub-sub.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleEvaluate());
      }

      // 2) Catch any cart mutation from any script (PDP add, drawer, apps…).
      const origFetch = window.fetch;
      const self = this;
      window.fetch = function (...args) {
        const result = origFetch.apply(this, args);
        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
          if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
            result.then(() => self.scheduleEvaluate()).catch(() => {});
          }
        } catch (e) {
          /* noop */
        }
        return result;
      };

      // 3) Manual hook for other integrations.
      document.addEventListener('tfg:refresh', () => this.scheduleEvaluate());
    }

    scheduleEvaluate() {
      if (this.busy) return; // ignore echoes from our own cart writes
      clearTimeout(this.evaluateTimer);
      this.evaluateTimer = setTimeout(() => this.evaluate(true), 250);
    }

    /* ---- Core evaluation ------------------------------------------------- */
    async evaluate(allowAutoOpen) {
      let cart;
      try {
        cart = await this.getCart();
      } catch (e) {
        return;
      }

      const giftLines = cart.items.filter((i) => this.isGiftLine(i));
      // Tier is based on the FULL cart subtotal, including the gift products'
      // prices (they are priced normally and check out free via the automatic
      // discount). So adding a product that brings the cart to a threshold —
      // whether a normal product or a popup gift — unlocks the next tier.
      const qualifyingSubtotal = this.cartSubtotal(cart);

      const tier = this.tierFor(qualifyingSubtotal);
      this.currentTier = tier;

      const giftCount = giftLines.reduce((sum, i) => sum + i.quantity, 0);
      const allowed = tier ? tier.quantity : 0;

      // Enforce the cap: remove excess / all gifts when the cart drops a tier.
      if (giftCount > allowed) {
        if (this.config.autoRemove || allowed === 0) {
          await this.trimGifts(cart, allowed);
          return; // trimGifts triggers a fresh cart update -> re-evaluate
        }
        this.promptRemoval(giftCount, allowed);
      }

      this.updateTriggerVisibility(tier, giftCount);

      // Refresh the property-driven lock target set + re-apply the quantity lock.
      this.updateGiftLineSets(cart);
      if (this.lockGiftQty) this.lockGiftQuantities();

      // Auto-open when a brand new (higher) tier is unlocked.
      if (allowAutoOpen && this.config.autoOpen && tier) {
        const seen = parseInt(sessionStorage.getItem(SEEN_TIER_KEY) || '0', 10);
        if (tier.threshold > seen && giftCount < allowed) {
          sessionStorage.setItem(SEEN_TIER_KEY, String(tier.threshold));
          this.open(cart);
        }
      }

      // Reset the "seen" marker if the customer falls out of all tiers.
      if (!tier) sessionStorage.removeItem(SEEN_TIER_KEY);

      // Keep an open modal in sync with the latest cart.
      if (this.overlay.classList.contains('is-open')) this.syncModal(cart);
    }

    tierFor(subtotal) {
      let match = null;
      for (const tier of this.config.tiers) {
        if (subtotal >= tier.threshold) match = tier;
      }
      return match;
    }

    /* Full cart subtotal (in cents) — the value the tier threshold is compared
       against. Includes gift products' prices. */
    cartSubtotal(cart) {
      if (cart && typeof cart.total_price === 'number') return cart.total_price;
      if (cart && cart.items) return cart.items.reduce((s, i) => s + i.final_line_price, 0);
      return 0;
    }

    isGiftLine(item) {
      return !!(item.properties && item.properties[GIFT_PROP]);
    }

    /* ---- Modal ----------------------------------------------------------- */
    async open(cart) {
      if (!this.currentTier) {
        try {
          cart = cart || (await this.getCart());
          this.currentTier = this.tierFor(this.cartSubtotal(cart));
        } catch (e) {
          /* noop */
        }
      }
      if (!this.currentTier) return; // nothing unlocked yet

      cart = cart || (await this.getCart().catch(() => null));
      this.renderCards();
      this.syncModal(cart);

      this.overlay.hidden = false;
      // next frame so the CSS transition runs
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
        document.body.classList.add('tfg-open');
        this.modal.focus();
      });
    }

    close() {
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('tfg-open');
      const onEnd = () => {
        this.overlay.hidden = true;
        this.overlay.removeEventListener('transitionend', onEnd);
      };
      this.overlay.addEventListener('transitionend', onEnd);
      // Fallback in case the transition doesn't fire.
      setTimeout(() => {
        if (!this.overlay.classList.contains('is-open')) this.overlay.hidden = true;
      }, 400);
    }

    renderCards() {
      this.grid.innerHTML = '';
      this.config.products.forEach((product) => {
        const node = this.cardTemplate.content.firstElementChild.cloneNode(true);
        node.dataset.variantId = product.variantId;

        const img = node.querySelector('[data-tfg-card-image]');
        if (product.image) {
          img.src = product.image;
          img.alt = product.title;
        } else {
          img.remove();
        }
        node.querySelector('[data-tfg-card-title]').textContent = product.title;

        const minus = node.querySelector('[data-tfg-minus]');
        const plus = node.querySelector('[data-tfg-plus]');
        minus.addEventListener('click', () => this.changeQty(product.variantId, -1));
        plus.addEventListener('click', () => this.changeQty(product.variantId, 1));

        if (!product.available) node.classList.add('is-disabled');

        this.grid.appendChild(node);
      });
    }

    /*
       Sync the modal with the cart. Gifts already claimed at a lower tier are
       "banked": they count toward the tier total but are NOT editable here.
       The popup only lets the customer pick the *remaining* allowance.
    */
    syncModal(cart) {
      this.selection = {};
      this.alreadyAdded = 0;
      if (cart) {
        this.alreadyAdded = cart.items
          .filter((i) => this.isGiftLine(i))
          .reduce((sum, i) => sum + i.quantity, 0);
      }
      this.paint();
    }

    /* Total the customer is entitled to at the current tier. */
    tierAllowance() {
      return this.currentTier ? this.currentTier.quantity : 0;
    }

    /* How many more they can still pick right now (tier total − already added). */
    remainingAllowance() {
      return Math.max(0, this.tierAllowance() - (this.alreadyAdded || 0));
    }

    changeQty(variantId, delta) {
      const remaining = this.remainingAllowance();
      const total = this.selectedCount();
      const current = this.selection[variantId] || 0;

      if (delta > 0 && total >= remaining) return; // remaining cap reached
      const next = Math.max(0, current + delta);
      if (next === 0) delete this.selection[variantId];
      else this.selection[variantId] = next;

      this.paint();
    }

    selectedCount() {
      return Object.values(this.selection).reduce((s, q) => s + q, 0);
    }

    paint() {
      const allowed = this.tierAllowance();
      const added = this.alreadyAdded || 0;
      const remaining = this.remainingAllowance();
      const total = this.selectedCount();
      const t = this.config.text;
      const thresholdMoney = this.formatMoney(this.currentTier ? this.currentTier.threshold : 0);

      // Header + contextual message.
      this.headingEl.textContent = t.heading;
      if (remaining <= 0) {
        // Everything for this tier is already claimed.
        this.subheadingEl.textContent = `You've added all ${allowed} free products for this tier. \u{1F389}`;
      } else if (added > 0) {
        // Upgraded to a higher tier with gifts already banked.
        this.subheadingEl.textContent =
          `You've reached ${thresholdMoney}! You can choose ${allowed} free products — ` +
          `${added} already added, ${remaining} more to go.`;
      } else {
        // First tier reached — use the merchant-configured copy.
        this.subheadingEl.textContent = t.subheading
          .replace('[threshold]', thresholdMoney)
          .replace('[quantity]', remaining);
      }

      // Cards.
      this.grid.querySelectorAll('[data-tfg-card]').forEach((card) => {
        const id = parseInt(card.dataset.variantId, 10);
        const qty = this.selection[id] || 0;
        card.querySelector('[data-tfg-qty]').textContent = qty;
        card.classList.toggle('is-selected', qty > 0);

        const plus = card.querySelector('[data-tfg-plus]');
        const minus = card.querySelector('[data-tfg-minus]');
        const unavailable = card.classList.contains('is-disabled');
        plus.disabled = unavailable || total >= remaining;
        minus.disabled = unavailable || qty === 0;
      });

      // Counter — show the banked / remaining breakdown when relevant.
      this.counterEl.textContent =
        added > 0
          ? `${added} added · selected ${total} of ${remaining} remaining`
          : `Selected ${total} / ${remaining}`;

      // Submit button.
      this.submitLabel.textContent = `${t.buttonLabel} (${total}/${remaining})`;
      const ready =
        remaining > 0 &&
        (this.config.requireFull ? total === remaining : total > 0 && total <= remaining);
      this.submitBtn.disabled = !ready;
    }

    /* ---- Cart mutations -------------------------------------------------- */
    async addFreeProducts() {
      if (this.submitBtn.disabled || this.busy) return;
      this.busy = true;
      this.submitBtn.classList.add('is-loading');

      try {
        // Append the newly chosen gifts. Any gifts already banked at a lower
        // tier stay in the cart; identical variant+property lines merge, so
        // quantities simply increment.
        const items = Object.entries(this.selection)
          .filter(([, qty]) => qty > 0)
          .map(([variantId, qty]) => ({
            id: Number(variantId),
            quantity: qty,
            properties: { [GIFT_PROP]: 'true' },
          }));

        if (items.length) {
          await fetch(`${this.cartRoute('add')}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
        }

        await this.refreshCartUI();
        this.close();
        this.openThemeCartDrawer();
      } catch (e) {
        console.error('[TieredFreeGift] Failed to add free products', e);
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.busy = false;
        // Allow auto-open: if the gifts just added pushed the cart total across
        // the next tier, its popup opens (the seen-tier guard stops re-opening
        // the same tier).
        this.evaluate(true);
      }
    }

    /* Reduce gifts down to `allowed`, newest gift lines removed first. */
    async trimGifts(cart, allowed) {
      this.busy = true;
      try {
        const giftLines = cart.items
          .map((item, index) => ({ item, line: index + 1 }))
          .filter((x) => this.isGiftLine(x.item));

        let remaining = allowed;
        // Keep the earliest lines up to `allowed`; zero out the rest.
        const updates = {};
        for (const { item } of giftLines) {
          if (remaining <= 0) {
            updates[item.key] = 0;
          } else if (item.quantity > remaining) {
            updates[item.key] = remaining;
            remaining = 0;
          } else {
            remaining -= item.quantity;
          }
        }

        if (Object.keys(updates).length) {
          await fetch(`${this.cartRoute('update')}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
          await this.refreshCartUI();
        }
      } catch (e) {
        console.error('[TieredFreeGift] Failed to trim gifts', e);
      } finally {
        this.busy = false;
        this.evaluate(false);
      }
    }

    promptRemoval(giftCount, allowed) {
      if (this._promptedFor === `${giftCount}:${allowed}`) return;
      this._promptedFor = `${giftCount}:${allowed}`;
      const keepMsg =
        allowed > 0
          ? `Your cart no longer qualifies for ${giftCount} free gifts. Only ${allowed} can be kept. Remove the extras?`
          : `Your cart no longer qualifies for free gifts. Remove them?`;
      // Non-blocking: use confirm so the customer stays in control.
      if (window.confirm(keepMsg)) {
        this.getCart().then((cart) => this.trimGifts(cart, allowed));
      }
    }

    /* ---- Cart helpers ---------------------------------------------------- */
    getCart() {
      return fetch(`${this.cartRoute('root')}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
    }

    cartRoute(kind) {
      const routes = (window.routes = window.routes || {});
      switch (kind) {
        case 'add':
          return routes.cart_add_url || '/cart/add.js';
        case 'change':
          return routes.cart_change_url || '/cart/change.js';
        case 'update':
          return routes.cart_update_url || '/cart/update.js';
        case 'root':
        default:
          return (routes.cart_url || '/cart').replace(/\.js$/, '');
      }
    }

    /* Re-render whichever cart UI this theme uses. */
    async refreshCartUI() {
      const cartPage = window.location.pathname === (window.routes && window.routes.cart_url);

      // GoCart drawer (active on this store) re-reads /cart and re-renders.
      if (window.goCart && typeof window.goCart.fetchCart === 'function') {
        try {
          window.goCart.fetchCart();
        } catch (e) {
          /* noop */
        }
      }

      // Native Dawn drawer + icon bubble, via the Sections API (no-op if absent).
      try {
        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const url = `${window.location.pathname}?sections=${sections.join(',')}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        this.replaceSection(data['cart-drawer'], '#CartDrawer');
        this.replaceSection(data['cart-icon-bubble'], '#cart-icon-bubble');
      } catch (e) {
        /* noop */
      }

      // Tell the rest of the theme the cart changed.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tiered-free-gift' });
      }
      document.dispatchEvent(new CustomEvent('tfg:cart-updated'));

      // The cart page has no drawer to patch — reload it to reflect changes.
      if (cartPage) window.location.reload();
    }

    replaceSection(html, selector) {
      if (!html) return;
      const target = document.querySelector(selector);
      if (!target) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    }

    openThemeCartDrawer() {
      // GoCart drawer (active on this store): re-render + open in one call.
      if (window.goCart && typeof window.goCart.fetchAndOpenCart === 'function') {
        try {
          window.goCart.fetchAndOpenCart();
          return;
        } catch (e) {
          /* fall through to native drawer */
        }
      }

      // Native Dawn drawer fallback.
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.classList.remove('is-empty');
        drawer.open();
      }
    }

    /* ---- Trigger visibility --------------------------------------------- */
    updateTriggerVisibility(tier, giftCount) {
      if (!this.trigger) return;
      const allowed = tier ? tier.quantity : 0;
      const incomplete = tier && giftCount < allowed;
      this.triggerLabel.textContent = this.config.text.triggerLabel;
      this.trigger.classList.toggle('is-visible', !!incomplete);
    }

    /* ---- Lock the quantity of `free_products` lines --------------------- */
    /*
       The quantity lock is driven ONLY by the line item property
       `free_products: true` — never by product ID, handle, variant, tag,
       collection, or type. We read which cart lines carry the property from
       /cart.js (updateGiftLineSets) and match those exact lines in the DOM by
       line key (preferred) or variant id (fallback).

       Theme-controlled carts (native Dawn drawer, /cart page, GoCart) already
       apply the lock server-side via the property, so this JS only needs to
       cover app-rendered drawers (PickyStory / Rebuy / Amplify) whose markup
       can't be edited in Liquid. A MutationObserver re-applies as they re-render.

       Note: if the SAME variant were added both as a free gift and normally, an
       app drawer that doesn't expose a per-line key can't tell the two lines
       apart and both would lock. Gift products are dedicated duplicates that
       aren't sold normally, so this doesn't occur in practice.
    */
    setupQuantityLock() {
      const run = () => this.lockGiftQuantities();
      if (document.readyState !== 'loading') run();
      else document.addEventListener('DOMContentLoaded', run);

      const observer = new MutationObserver(() => {
        clearTimeout(this._lockTimer);
        this._lockTimer = setTimeout(() => this.lockGiftQuantities(), 150);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    /* Rebuild the property-driven target sets from the current cart. */
    updateGiftLineSets(cart) {
      if (!this.giftLineVariants) return;
      this.cartItems = cart.items || [];
      this.giftLineVariants.clear();
      this.giftLineKeys.clear();
      this.ambiguousVariants.clear();

      const variantCount = {};
      this.cartItems.forEach((i) => {
        const v = String(i.variant_id);
        variantCount[v] = (variantCount[v] || 0) + 1;
      });
      Object.keys(variantCount).forEach((v) => {
        if (variantCount[v] > 1) this.ambiguousVariants.add(v);
      });

      this.cartItems.filter((i) => this.isGiftLine(i)).forEach((i) => {
        if (i.variant_id != null) this.giftLineVariants.add(String(i.variant_id));
        if (i.key) this.giftLineKeys.add(String(i.key));
      });
    }

    lockGiftQuantities() {
      if (!this.lockGiftQty || !this.cartItems || !this.cartItems.length) return;

      // ---- 1) Precise: position-map DOM lines to /cart.js items. -----------
      // App drawers render one element per cart line in cart order. When the
      // count matches and the readable variant IDs line up, we can lock the
      // EXACT line whose cart item carries `free_products` — even when another
      // line shares the same variant (e.g. the same product added normally).
      const PRIMARY_SELECTORS = [
        '.picky-cart-item',
        '.rebuy-cart__flyout-item',
        '.amplify-cart-item',
        '[data-line-item]',
      ];
      for (const sel of PRIMARY_SELECTORS) {
        const lines = Array.from(document.querySelectorAll(sel));
        if (!lines.length || lines.length !== this.cartItems.length) continue;

        // Verify DOM order matches cart order by variant (where readable).
        let aligned = true;
        for (let i = 0; i < lines.length; i += 1) {
          const vid = this.lineVariantId(lines[i]);
          if (vid && String(vid) !== String(this.cartItems[i].variant_id)) {
            aligned = false;
            break;
          }
        }
        if (!aligned) continue;

        lines.forEach((line, i) => {
          if (this.isGiftLine(this.cartItems[i])) this.lockLineQuantity(line);
          else this.unlockLineQuantity(line);
        });
        return;
      }

      // ---- 2) Fallback: match by line key, or by an UNAMBIGUOUS variant. ---
      // Never lock via a variant that appears on more than one cart line, so a
      // normally-added product of the same variant is never affected.
      if (!this.giftLineVariants.size && !this.giftLineKeys.size) return;
      const APP_LINE_SELECTORS = [
        '.picky-cart-item', '[class*="picky-cart-item"]',
        '.rebuy-cart__flyout-item', '.rebuy-cart-item', '[class*="cart__flyout-item"]',
        '.amplify-cart-item', '.amplify-item', '[class*="amplify"][class*="item"]',
        '[data-line-item]', '[class*="line-item"]', '[class*="line_item"]',
      ].join(',');

      document.querySelectorAll(APP_LINE_SELECTORS).forEach((line) => {
        const key = this.lineKey(line);
        const vid = this.lineVariantId(line);
        const byKey = key && this.giftLineKeys.has(String(key));
        const byVariant =
          vid && this.giftLineVariants.has(String(vid)) && !this.ambiguousVariants.has(String(vid));
        if (byKey || byVariant) this.lockLineQuantity(line);
        else this.unlockLineQuantity(line);
      });
    }

    /* Best-effort read of a line item key from common attributes. */
    lineKey(line) {
      const attrs = [
        'data-key', 'data-line-item-key', 'data-cart-item-key',
        'data-item-key', 'data-line-key', 'data-line_item_key',
      ];
      for (const a of attrs) {
        const v = line.getAttribute && line.getAttribute(a);
        if (v) return v;
        const child = line.querySelector(`[${a}]`);
        if (child) return child.getAttribute(a);
      }
      return null;
    }

    /* Best-effort read of a line item's variant ID from common attributes. */
    lineVariantId(line) {
      const attrs = [
        'data-selected-variant-id',
        'data-variant-id',
        'data-variant',
        'data-variantid',
        'data-variant_id',
        'data-id',
      ];
      for (const a of attrs) {
        const v = line.getAttribute && line.getAttribute(a);
        if (v && /^\d+$/.test(v.trim())) return v.trim();
        const child = line.querySelector(`[${a}]`);
        if (child) {
          const cv = child.getAttribute(a);
          if (cv && /^\d+$/.test(cv.trim())) return cv.trim();
        }
      }
      // Dawn cart page: quantity input carries the variant id.
      const qtyInput = line.querySelector('[data-quantity-variant-id]');
      if (qtyInput) return qtyInput.getAttribute('data-quantity-variant-id');
      // Fallback: a product link with ?variant=.
      const link = line.querySelector('a[href*="variant="]');
      if (link) {
        const m = link.getAttribute('href').match(/variant=(\d+)/);
        if (m) return m[1];
      }
      return null;
    }

    lockLineQuantity(line) {
      if (line.classList.contains('tfg-line-gift')) return; // already locked (idempotent)

      // 1) Hide +/- controls identified by class/attribute patterns.
      const stepSelectors = [
        'button[name="plus"]', 'button[name="minus"]',
        '[data-action="increase"]', '[data-action="decrease"]',
        '[class*="increment"]', '[class*="decrement"]',
        '[class*="quantity"] button', '[class*="qty"] button',
        '[class*="quantity"] a', '[class*="qty"] a',
        '[class*="plus"]', '[class*="minus"]',
        '.quantity__button',
      ].join(',');
      line.querySelectorAll(stepSelectors).forEach((el) => el.classList.add('tfg-qty-hidden'));

      // 2) Hide text-based +/- steppers, but only interactive elements.
      line.querySelectorAll('button, a, [role="button"]').forEach((el) => {
        if (el.children.length) return;
        const t = (el.textContent || '').trim();
        if (t === '+' || t === '-' || t === '−' || t === '–') {
          el.classList.add('tfg-qty-hidden');
        }
      });

      // 3) Make the quantity input non-editable.
      line
        .querySelectorAll(
          'input[type="number"], input[name="updates[]"], [class*="quantity"] input, [class*="qty"] input'
        )
        .forEach((inp) => {
          inp.setAttribute('readonly', 'readonly');
          inp.setAttribute('disabled', 'disabled');
          inp.classList.add('tfg-qty-readonly');
        });

      line.classList.add('tfg-line-gift');
    }

    /* Reverse lockLineQuantity for a line that is NOT a gift (only undoing
       what we added: our own `tfg-*` classes / readonly attributes). */
    unlockLineQuantity(line) {
      if (!line.classList.contains('tfg-line-gift')) return;
      line.classList.remove('tfg-line-gift');
      line.querySelectorAll('.tfg-qty-hidden').forEach((el) => el.classList.remove('tfg-qty-hidden'));
      line.querySelectorAll('.tfg-qty-readonly').forEach((inp) => {
        inp.classList.remove('tfg-qty-readonly');
        inp.removeAttribute('readonly');
        inp.removeAttribute('disabled');
      });
    }

    /* ---- Money ----------------------------------------------------------- */
    formatMoney(cents) {
      const amount = (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: (cents % 100 === 0) ? 0 : 2,
        maximumFractionDigits: 2,
      });
      // Replace the money format placeholder with the computed amount.
      return this.moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount) || `$${amount}`;
    }
  }

  customElements.define('tiered-free-gift', TieredFreeGift);
})();
